import {
  queryOptions,
  type QueryClient,
  type QueryKey,
} from "@tanstack/react-query";
import { shouldRetryQuery } from "@softmax/web-features/data";
import { isRouteAccessDenied } from "@/session/route-query";
import { getWatchEntry } from "./watch.functions";
import { browserWatchApi } from "./browser-api";
import { featurableRoundStatusQuery } from "@/lib/leagues/featured-pool";
import type {
  DivisionPublic,
  RoundListPublic,
} from "@softmax/observatory-client/types";
import { watchRoundLookback, roundEpisodePageSize } from "@/lib/leagues/watch";
import {
  leagueCatalogQueryOptions,
  roundEpisodesPageQueryOptions,
} from "@/features/leagues/records";
export function watchEntryQueryOptions(selection: {
  leagueId: string | null;
  stored: string | null;
  globalKiosk: boolean;
}) {
  return queryOptions({
    queryKey: [
      "watch",
      "entry",
      selection.leagueId,
      selection.stored,
      selection.globalKiosk,
    ],
    queryFn: async ({ signal }) => await getWatchEntry({ selection, signal }),
    retry: (count, error: Error) =>
      !isRouteAccessDenied(error) && shouldRetryQuery(count, error),
    throwOnError: isRouteAccessDenied,
    staleTime: 60_000,
  });
}
export function watchDivisionsQueryOptions(leagueId: string) {
  return queryOptions({
    queryKey: [
      "divisions",
      "list",
      { leagueId, type: "competition", elevatedPrivileges: false },
    ] as const,
    queryFn: ({ signal }) =>
      browserWatchApi.get<DivisionPublic[]>(
        `/v2/divisions?league_id=${encodeURIComponent(leagueId)}&type=competition`,
        { signal },
      ),
    staleTime: 30000,
  });
}
export function watchRoundsQueryOptions(divisionId: string) {
  return queryOptions({
    queryKey: [
      "rounds",
      "list",
      {
        divisionId,
        statuses: featurableRoundStatusQuery,
        limit: watchRoundLookback,
        elevatedPrivileges: false,
      },
    ] as const,
    queryFn: ({ signal }) =>
      browserWatchApi.get<RoundListPublic>(
        `/v2/rounds?division_id=${encodeURIComponent(divisionId)}&${featurableRoundStatusQuery}&limit=${watchRoundLookback}`,
        { signal },
      ),
    staleTime: 30000,
  });
}

export function watchRoundEpisodesQueryOptions({
  roundId,
  cursor,
}: {
  roundId: string;
  cursor?: string;
}) {
  const request: { roundId: string; limit: number; cursor?: string } = {
    roundId,
    limit: roundEpisodePageSize,
  };

  if (cursor !== undefined) request.cursor = cursor;

  return roundEpisodesPageQueryOptions(request);
}

export function watchSeedRejection({
  client,
  queryKey,
  data,
}: {
  client: QueryClient;
  queryKey: QueryKey;
  data: unknown;
}) {
  const cache = client.getQueryCache();
  const source = cache.find({ queryKey, exact: true });
  const version = source?.state.dataUpdateCount;
  return async (error: Error) => {
    const currentSource = () =>
      source !== undefined &&
      cache.find({ queryKey, exact: true }) === source &&
      source.state.data === data &&
      source.state.dataUpdateCount === version;

    const catalogKey = leagueCatalogQueryOptions().queryKey;
    const catalog = cache.find({ queryKey: catalogKey, exact: true });

    if (source && currentSource()) {
      await source.cancel({ silent: true });

      if (currentSource())
        source.setState({
          data: undefined,
          dataUpdatedAt: 0,
          error,
          errorUpdatedAt: Date.now(),
          errorUpdateCount: source.state.errorUpdateCount + 1,
          status: "error",
          fetchStatus: "idle",
        });
    }

    await client.resetQueries({
      queryKey: catalogKey,
      exact: true,
      predicate: (query) => query === catalog && query.state.error === error,
    });
  };
}
